/*****************************************************
 * PrintDialog.jsx — v4.0  "Bovenmenselijk"
 * Live A4 preview + 5 rapporttypes
 *****************************************************/
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { generateHTML, generateWordHTML } from './printUtils';

const G = {
  navy:'#1a364c', gold:'#d4a848', white:'#ffffff',
  borderL:'#e8edf3', cream:'#f8fafc',
  green:'#10b981', red:'#ef4444', amber:'#f59e0b',
  txt:'#1e293b', txt2:'#64748b', txt3:'#94a3b8',
  blue:'#3b82f6',
};

const NU = new Date();
const ditJaar  = NU.getFullYear();
const ditMaand = NU.getMonth();

function isoDate(d) { return d.toISOString().slice(0,10); }
function startWeek()  { const d=new Date(NU); d.setDate(NU.getDate()-NU.getDay()+1); return isoDate(d); }
function startMaand() { return `${ditJaar}-${String(ditMaand+1).padStart(2,'0')}-01`; }
function startKwart() { return `${ditJaar}-${String(Math.floor(ditMaand/3)*3+1).padStart(2,'0')}-01`; }
function startJaar()  { return `${ditJaar}-01-01`; }
function vandaag()    { return isoDate(NU); }

function isOnderweg(a) {
  const sl=(a.Status||'').toLowerCase();
  if(sl.includes('onderweg')) return true;
  if((sl.includes('opdracht verzonden')||sl.includes('opdracht bevestigd'))&&a.LaadDatum){
    const l=new Date(a.LaadDatum); l.setHours(0,0,0,0);
    const v=new Date(); v.setHours(0,0,0,0);
    return l<=v;
  }
  return false;
}
function statusLabel(a) {
  const sl=(a.Status||'').toLowerCase();
  if(sl.includes('afgerond')||sl.includes('afgeleverd')) return 'Afgeleverd';
  if(isOnderweg(a)) return 'Onderweg';
  if(sl.includes('geoffreerd')) return 'Prijs ontvangen';
  if(sl.includes('opdracht verzonden')||sl.includes('opdracht bevestigd')) return 'Bevestigd';
  if(sl.includes('opdracht ontvangen')) return 'Akkoord gegeven';
  return 'In behandeling';
}

// ─── UI helpers ───────────────────────────────────────────────────────────────
const Label = ({children}) => (
  <div style={{fontSize:11,fontWeight:600,color:G.txt2,marginBottom:5,letterSpacing:'.03em'}}>{children}</div>
);
const Select = ({children,...p}) => (
  <select {...p} style={{width:'100%',padding:'7px 10px',border:`1px solid ${G.borderL}`,borderRadius:6,
    fontSize:12.5,fontFamily:'inherit',background:G.white,cursor:'pointer',...(p.style||{})}}>
    {children}
  </select>
);
const Input = (p) => (
  <input {...p} style={{width:'100%',padding:'7px 10px',border:`1px solid ${G.borderL}`,borderRadius:6,
    fontSize:12.5,fontFamily:'inherit',boxSizing:'border-box',...(p.style||{})}}/>
);
const Check = ({label,sub,checked,onChange}) => (
  <label style={{display:'flex',alignItems:'flex-start',gap:8,cursor:'pointer',marginBottom:7}}>
    <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)}
      style={{marginTop:2,accentColor:G.navy,width:14,height:14,flexShrink:0}}/>
    <div>
      <div style={{fontSize:12.5,color:G.txt,fontWeight:500}}>{label}</div>
      {sub&&<div style={{fontSize:11,color:G.txt3,marginTop:1}}>{sub}</div>}
    </div>
  </label>
);
const PeriodKnoppen = ({van,setVan,tot,setTot,actief,setActief}) => {
  const presets = [
    {l:'Week',      v:startWeek(),             t:vandaag()},
    {l:'Maand',     v:startMaand(),            t:vandaag()},
    {l:'Kwartaal',  v:startKwart(),            t:vandaag()},
    {l:'Dit jaar',  v:startJaar(),             t:vandaag()},
    {l:'Vorig jaar',v:`${ditJaar-1}-01-01`,    t:`${ditJaar-1}-12-31`},
    {l:'Alles',     v:'',                      t:''},
  ];
  return (
    <div>
      <Label>Periode</Label>
      <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:8}}>
        {presets.map(p=>(
          <button key={p.l} onClick={()=>{setVan(p.v);setTot(p.t);if(setActief)setActief(p.l);}} style={{
            padding:'3px 10px',borderRadius:20,cursor:'pointer',fontSize:11.5,
            border:`1px solid ${actief===p.l?G.navy:G.borderL}`,
            background:actief===p.l?G.navy:G.white,
            color:actief===p.l?'#fff':G.txt2,
            fontWeight:actief===p.l?600:400,
          }}>{p.l}</button>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
        <div><Label>Van</Label><Input type="date" value={van} onChange={e=>setVan(e.target.value)}/></div>
        <div><Label>Tot</Label><Input type="date" value={tot} onChange={e=>setTot(e.target.value)}/></div>
      </div>
    </div>
  );
};

// ─── Rapport types definitie ──────────────────────────────────────────────────
const RAPPORT_TYPES = [
  {
    id: 'dashboard',
    icon: '📊',
    titel: 'Managementrapport',
    sub: 'KPI\'s, trends en top routes. Ideaal voor CEO of directie.',
    secties: ['home','analytics'],
  },
  {
    id: 'zendingen',
    icon: '📋',
    titel: 'Zendingenoverzicht',
    sub: 'Gefilterde lijst met subtotalen. Voor logistiek manager.',
    secties: ['home','aanvragen','tracking','analytics'],
  },
  {
    id: 'maandrapport',
    icon: '📅',
    titel: 'Maandrapport',
    sub: 'Maand-voor-maand vergelijking met vorige periode.',
    secties: ['home','analytics'],
  },
  {
    id: 'jaaroverzicht',
    icon: '🗓',
    titel: 'Jaaroverzicht',
    sub: 'Volledig jaaroverzicht met vergelijking vorig jaar.',
    secties: ['home','analytics'],
  },
  {
    id: 'zending_detail',
    icon: '📄',
    titel: 'Zending dossier',
    sub: 'Compleet dossier van één zending.',
    secties: ['home','aanvragen','tracking'],
  },
  {
    id: 'facturen',
    icon: '🧾',
    titel: 'Factuuroverzicht',
    sub: 'Financieel overzicht met openstaande en betaalde facturen.',
    secties: ['home','facturen'],
  },
];

// ─── Configuratiepanelen per type ─────────────────────────────────────────────
// Herbruikbare grafiektype-selector
const GrafiekKeuze = ({waarde, onChange, opties}) => (
  <div style={{marginLeft:22,marginTop:2,marginBottom:4,display:'flex',gap:4,flexWrap:'wrap'}}>
    {opties.map(([type,lbl])=>(
      <button key={type} onClick={()=>onChange(type)} style={{
        padding:'2px 9px',borderRadius:20,cursor:'pointer',fontSize:10.5,
        border:`1px solid ${waarde===type?G.navy:G.borderL}`,
        background:waarde===type?G.navy:G.white,
        color:waarde===type?'#fff':G.txt2,
        fontWeight:waarde===type?600:400,
      }}>{lbl}</button>
    ))}
  </div>
);

function ConfigDashboard({cfg, setCfg}) {
  const periodeType = cfg.dashPeriodeType || 'jaar';
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div>
        <Label>Periode</Label>
        <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:8}}>
          {[['jaar','Jaar'],['kwartaal','Kwartaal'],['maand','Maand'],['custom','Aangepast']].map(([type,lbl])=>(
            <button key={type} onClick={()=>setCfg(c=>({...c,dashPeriodeType:type}))} style={{
              padding:'4px 11px',borderRadius:20,cursor:'pointer',fontSize:11.5,
              border:`1px solid ${periodeType===type?G.navy:G.borderL}`,
              background:periodeType===type?G.navy:G.white,
              color:periodeType===type?'#fff':G.txt2,fontWeight:periodeType===type?600:400,
            }}>{lbl}</button>
          ))}
        </div>

        {periodeType==='jaar' && (
          <div style={{display:'flex',gap:6}}>
            {[ditJaar-2,ditJaar-1,ditJaar].map(j=>(
              <button key={j} onClick={()=>setCfg(c=>({...c,jaar:j}))} style={{
                padding:'4px 12px',borderRadius:20,cursor:'pointer',fontSize:11.5,
                border:`1px solid ${cfg.jaar===j?G.navy:G.borderL}`,
                background:cfg.jaar===j?G.navy:G.white,
                color:cfg.jaar===j?'#fff':G.txt2,fontWeight:cfg.jaar===j?600:400,
              }}>{j}</button>
            ))}
          </div>
        )}

        {periodeType==='kwartaal' && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            <div>
              <Label>Jaar</Label>
              <Select value={cfg.jaar||ditJaar} onChange={e=>setCfg(c=>({...c,jaar:parseInt(e.target.value)}))}>
                {[ditJaar-2,ditJaar-1,ditJaar].map(j=><option key={j} value={j}>{j}</option>)}
              </Select>
            </div>
            <div>
              <Label>Kwartaal</Label>
              <Select value={cfg.kwartaal||1} onChange={e=>setCfg(c=>({...c,kwartaal:parseInt(e.target.value)}))}>
                <option value={1}>Q1 (jan–mrt)</option>
                <option value={2}>Q2 (apr–jun)</option>
                <option value={3}>Q3 (jul–sep)</option>
                <option value={4}>Q4 (okt–dec)</option>
              </Select>
            </div>
          </div>
        )}

        {periodeType==='maand' && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            <div>
              <Label>Jaar</Label>
              <Select value={cfg.jaar||ditJaar} onChange={e=>setCfg(c=>({...c,jaar:parseInt(e.target.value)}))}>
                {[ditJaar-2,ditJaar-1,ditJaar].map(j=><option key={j} value={j}>{j}</option>)}
              </Select>
            </div>
            <div>
              <Label>Maand</Label>
              <Select value={cfg.maand??ditMaand} onChange={e=>setCfg(c=>({...c,maand:parseInt(e.target.value)}))}>
                {['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'].map((m,i)=>(
                  <option key={i} value={i}>{m}</option>
                ))}
              </Select>
            </div>
          </div>
        )}

        {periodeType==='custom' && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <div><Label>Van</Label><Input type="date" value={cfg.dashVan||''} onChange={e=>setCfg(c=>({...c,dashVan:e.target.value}))}/></div>
            <div><Label>Tot</Label><Input type="date" value={cfg.dashTot||''} onChange={e=>setCfg(c=>({...c,dashTot:e.target.value}))}/></div>
          </div>
        )}
      </div>
      <div>
        <Label>Onderdelen</Label>
        <Check label="KPI samenvatting" sub="Kosten, volume, gemiddelden" checked={cfg.inclKpi} onChange={v=>setCfg(c=>({...c,inclKpi:v}))}/>
        <Check label="Kostengrafiek per maand" sub="Visuele trendlijn" checked={cfg.inclGrafiek} onChange={v=>setCfg(c=>({...c,inclGrafiek:v}))}/>
        {cfg.inclGrafiek && (
          <div style={{marginLeft:22,marginTop:4,marginBottom:4}}>
            <Label>Grafiektype</Label>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {[['balk','📊 Balk'],['lijn','📈 Lijn'],['taart','🥧 Taart'],['donut','⭕ Donut']].map(([type,lbl])=>(
                <button key={type} onClick={()=>setCfg(c=>({...c,grafiekType:type}))} style={{
                  padding:'3px 10px',borderRadius:20,cursor:'pointer',fontSize:11,
                  border:`1px solid ${(cfg.grafiekType||'balk')===type?G.navy:G.borderL}`,
                  background:(cfg.grafiekType||'balk')===type?G.navy:G.white,
                  color:(cfg.grafiekType||'balk')===type?'#fff':G.txt2,
                  fontWeight:(cfg.grafiekType||'balk')===type?600:400,
                }}>{lbl}</button>
              ))}
            </div>
          </div>
        )}
        <Check label="Top bestemmingen" sub="Meest gebruikte landen" checked={cfg.inclBestemming} onChange={v=>setCfg(c=>({...c,inclBestemming:v}))}/>
        {cfg.inclBestemming && (
          <GrafiekKeuze waarde={cfg.grafiekTypeBestemming||'hbalk'} onChange={v=>setCfg(c=>({...c,grafiekTypeBestemming:v}))} opties={[['hbalk','▬ Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
        )}
        <Check label="Top routes" sub="Laad → Los combinaties" checked={cfg.inclRoutes} onChange={v=>setCfg(c=>({...c,inclRoutes:v}))}/>
        {cfg.inclRoutes && (
          <GrafiekKeuze waarde={cfg.grafiekTypeRoutes||'hbalk'} onChange={v=>setCfg(c=>({...c,grafiekTypeRoutes:v}))} opties={[['hbalk','▬ Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
        )}
        <Check label="Goederensoorten" sub="Wat u het meest vervoert" checked={cfg.inclGoed} onChange={v=>setCfg(c=>({...c,inclGoed:v}))}/>
        {cfg.inclGoed && (
          <GrafiekKeuze waarde={cfg.grafiekTypeGoed||'taart'} onChange={v=>setCfg(c=>({...c,grafiekTypeGoed:v}))} opties={[['hbalk','▬ Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
        )}
        <Check label="Vergelijking vorig jaar" sub="% verschil tonen" checked={cfg.inclVergelijk} onChange={v=>setCfg(c=>({...c,inclVergelijk:v}))}/>
      </div>
    </div>
  );
}

function ConfigZendingen({cfg, setCfg}) {
  const alleStatussen = ['Afgeleverd','Onderweg','Bevestigd','Prijs ontvangen','Akkoord gegeven','In behandeling'];
  const toggleStatus = (s) => {
    const curr = cfg.statussen||['alle'];
    if(s==='alle'){setCfg(c=>({...c,statussen:['alle']}));return;}
    const next = curr.filter(x=>x!=='alle').includes(s)
      ? curr.filter(x=>x!==s) : [...curr.filter(x=>x!=='alle'),s];
    setCfg(c=>({...c,statussen:next.length===0?['alle']:next}));
  };
  const st = cfg.statussen||['alle'];
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <PeriodKnoppen van={cfg.van||''} setVan={v=>setCfg(c=>({...c,van:v}))} tot={cfg.tot||''} setTot={v=>setCfg(c=>({...c,tot:v}))}/>
      <div>
        <Label>Status</Label>
        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
          {['alle',...alleStatussen].map(s=>(
            <button key={s} onClick={()=>toggleStatus(s)} style={{
              padding:'3px 9px',borderRadius:20,cursor:'pointer',fontSize:11,
              border:`1px solid ${st.includes(s)?G.navy:G.borderL}`,
              background:st.includes(s)?G.navy:G.white,
              color:st.includes(s)?'#fff':G.txt2,
            }}>{s==='alle'?'Alle':s}</button>
          ))}
        </div>
      </div>
      <div>
        <Label>Sortering</Label>
        <Select value={cfg.sorteer||'datum-desc'} onChange={e=>setCfg(c=>({...c,sorteer:e.target.value}))}>
          <option value="datum-desc">Datum (nieuwste eerst)</option>
          <option value="datum-asc">Datum (oudste eerst)</option>
          <option value="status">Status</option>
          <option value="kosten-desc">Kosten (hoog-laag)</option>
        </Select>
      </div>
      <div>
        <Label>Kolommen</Label>
        <Check label="Gewicht" checked={cfg.inclGewicht!==false} onChange={v=>setCfg(c=>({...c,inclGewicht:v}))}/>
        <Check label="Kosten" checked={cfg.inclKosten!==false} onChange={v=>setCfg(c=>({...c,inclKosten:v}))}/>
      </div>
    </div>
  );
}

function ConfigMaandrapport({cfg, setCfg}) {
  const maandNamen = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div>
        <Label>Jaar</Label>
        <div style={{display:'flex',gap:6}}>
          {[ditJaar-2,ditJaar-1,ditJaar].map(j=>(
            <button key={j} onClick={()=>setCfg(c=>({...c,jaar:j}))} style={{
              padding:'5px 14px',borderRadius:20,cursor:'pointer',fontSize:12.5,
              border:`1px solid ${cfg.jaar===j?G.navy:G.borderL}`,
              background:cfg.jaar===j?G.navy:G.white,
              color:cfg.jaar===j?'#fff':G.txt2,fontWeight:cfg.jaar===j?600:400,
            }}>{j}</button>
          ))}
        </div>
      </div>
      <div>
        <Label>Maand</Label>
        <Select value={cfg.maand??ditMaand} onChange={e=>setCfg(c=>({...c,maand:parseInt(e.target.value)}))}>
          {maandNamen.map((m,i)=><option key={i} value={i}>{m}</option>)}
        </Select>
      </div>
      <div>
        <Label>Vergelijken met</Label>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {[['nee','Nee'],['vorigeMaand','Vorige maand'],['vorigJaar','Zelfde maand vorig jaar']].map(([v,l])=>(
            <button key={v} onClick={()=>setCfg(c=>({...c,vergelijkModus:v}))} style={{
              padding:'4px 10px',borderRadius:20,cursor:'pointer',fontSize:11,
              border:`1px solid ${(cfg.vergelijkModus||'vorigeMaand')===v?G.navy:G.borderL}`,
              background:(cfg.vergelijkModus||'vorigeMaand')===v?G.navy:G.white,
              color:(cfg.vergelijkModus||'vorigeMaand')===v?'#fff':G.txt2,
            }}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label>Kostengrafiek type</Label>
        <GrafiekKeuze waarde={cfg.grafiekType||'balk3d'} onChange={v=>setCfg(c=>({...c,grafiekType:v}))}
          opties={[['balk3d','🧊 3D Balk'],['balk','📊 Balk'],['lijn','📈 Lijn'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
      </div>
      <div>
        <Label>Onderdelen</Label>
        <Check label="Zendingen tabel" checked={cfg.inclMaand!==false} onChange={v=>setCfg(c=>({...c,inclMaand:v}))}/>
        <Check label="Bestemmingen" checked={cfg.inclBestemming!==false} onChange={v=>setCfg(c=>({...c,inclBestemming:v}))}/>
        {cfg.inclBestemming!==false && <GrafiekKeuze waarde={cfg.grafiekTypeBestemming||'hbalk'} onChange={v=>setCfg(c=>({...c,grafiekTypeBestemming:v}))}
          opties={[['hbalk','📊 Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>}
        <Check label="Goederensoorten" checked={cfg.inclGoed!==false} onChange={v=>setCfg(c=>({...c,inclGoed:v}))}/>
        {cfg.inclGoed!==false && <GrafiekKeuze waarde={cfg.grafiekTypeGoed||'taart'} onChange={v=>setCfg(c=>({...c,grafiekTypeGoed:v}))}
          opties={[['taart','🥧 Taart'],['donut','⭕ Donut'],['hbalk','📊 Balk']]}/>}
      </div>
    </div>
  );
}

function ConfigJaaroverzicht({cfg, setCfg}) {
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div>
        <Label>Jaar</Label>
        <div style={{display:'flex',gap:6}}>
          {[ditJaar-2,ditJaar-1,ditJaar].map(j=>(
            <button key={j} onClick={()=>setCfg(c=>({...c,jaar:j}))} style={{
              padding:'5px 14px',borderRadius:20,cursor:'pointer',fontSize:12.5,
              border:`1px solid ${cfg.jaar===j?G.navy:G.borderL}`,
              background:cfg.jaar===j?G.navy:G.white,
              color:cfg.jaar===j?'#fff':G.txt2,fontWeight:cfg.jaar===j?600:400,
            }}>{j}</button>
          ))}
        </div>
      </div>
      <div>
        <Label>Vergelijking vorig jaar</Label>
        <div style={{display:'flex',gap:6}}>
          {[['ja','Ja'],['nee','Nee']].map(([v,l])=>(
            <button key={v} onClick={()=>setCfg(c=>({...c,inclVergelijk:v==='ja'}))} style={{
              padding:'4px 14px',borderRadius:20,cursor:'pointer',fontSize:12,
              border:`1px solid ${(cfg.inclVergelijk!==false?'ja':'nee')===v?G.navy:G.borderL}`,
              background:(cfg.inclVergelijk!==false?'ja':'nee')===v?G.navy:G.white,
              color:(cfg.inclVergelijk!==false?'ja':'nee')===v?'#fff':G.txt2,
            }}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label>Grafiektype kosten per maand</Label>
        <GrafiekKeuze waarde={cfg.grafiekType||'balk3d'} onChange={v=>setCfg(c=>({...c,grafiekType:v}))}
          opties={[['balk3d','🧊 3D'],['balk','📊 Balk'],['lijn','📈 Lijn'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
      </div>
      <div>
        <Label>Grafiektype bestemmingen</Label>
        <GrafiekKeuze waarde={cfg.grafiekTypeBestemming||'hbalk'} onChange={v=>setCfg(c=>({...c,grafiekTypeBestemming:v}))}
          opties={[['hbalk','▬ Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
      </div>
      <div>
        <Label>Grafiektype routes</Label>
        <GrafiekKeuze waarde={cfg.grafiekTypeRoutes||'hbalk'} onChange={v=>setCfg(c=>({...c,grafiekTypeRoutes:v}))}
          opties={[['hbalk','▬ Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
      </div>
      <div>
        <Label>Grafiektype goederensoorten</Label>
        <GrafiekKeuze waarde={cfg.grafiekTypeGoed||'taart'} onChange={v=>setCfg(c=>({...c,grafiekTypeGoed:v}))}
          opties={[['hbalk','▬ Balk'],['taart','🥧 Taart'],['donut','⭕ Donut']]}/>
      </div>
      <div>
        <Label>Onderdelen</Label>
        <Check label="KPI samenvatting" checked={cfg.inclKpi!==false} onChange={v=>setCfg(c=>({...c,inclKpi:v}))}/>
        <Check label="Kosten per maand grafiek" checked={cfg.inclGrafiek!==false} onChange={v=>setCfg(c=>({...c,inclGrafiek:v}))}/>
        <Check label="Maandtabel" checked={cfg.inclMaand!==false} onChange={v=>setCfg(c=>({...c,inclMaand:v}))}/>
        <Check label="Bestemmingen & routes" checked={cfg.inclBestemming!==false} onChange={v=>setCfg(c=>({...c,inclBestemming:v}))}/>
        <Check label="Goederensoorten" checked={cfg.inclGoed!==false} onChange={v=>setCfg(c=>({...c,inclGoed:v}))}/>
        <Check label="Gewicht & colli" checked={cfg.inclGewicht!==false} onChange={v=>setCfg(c=>({...c,inclGewicht:v}))}/>
      </div>
    </div>
  );
}

function ConfigZendingDetail({cfg, setCfg, aanvragen}) {
  const [zoek, setZoek] = useState('');
  const [filter, setFilter] = useState('alle');
  const [vanD, setVanD] = useState('');
  const [totD, setTotD] = useState('');

  const nu = new Date();
  const ditJaarZ = nu.getFullYear();
  const ditMaandZ = nu.getMonth();
  const kwartaal = Math.floor(ditMaandZ / 3);
  const startWeek = new Date(nu); startWeek.setDate(nu.getDate() - nu.getDay() + 1);
  const startKwartaalDate = new Date(ditJaarZ, kwartaal * 3, 1);

  const gefilterd = useMemo(() => {
    let l = [...aanvragen].sort((a,b) =>
      (b.Datum||b.LaadDatum||'').localeCompare(a.Datum||a.LaadDatum||'')
    );
    const datumVan = d => new Date(d.Datum||d.LaadDatum||0);
    if(filter === 'deze-week')   l = l.filter(a => datumVan(a) >= startWeek);
    else if(filter === 'deze-maand')  l = l.filter(a => { const d=datumVan(a); return d.getFullYear()===ditJaarZ && d.getMonth()===ditMaandZ; });
    else if(filter === 'dit-kwartaal') l = l.filter(a => datumVan(a) >= startKwartaalDate);
    else if(filter === 'lopend') l = l.filter(a => { const s=(a.Status||'').toLowerCase(); return s.includes('onderweg')||s.includes('opdracht')||s.includes('akkoord'); });
    else if(filter === 'aangepast') {
      if(vanD) l = l.filter(a => (a.Datum||a.LaadDatum||'') >= vanD);
      if(totD) l = l.filter(a => (a.Datum||a.LaadDatum||'') <= totD);
    }
    return l;
  }, [aanvragen, filter, vanD, totD, ditJaarZ, ditMaandZ, kwartaal]);

  const lijst = useMemo(() => {
    if(!zoek) return gefilterd.slice(0, 50);
    const q = zoek.toLowerCase();
    return gefilterd.filter(a =>
      (a.AanvraagID||'').toLowerCase().includes(q) ||
      (a.LaadPlaats||a.LaadLand||'').toLowerCase().includes(q) ||
      (a.LosPlaats||a.LosLand||'').toLowerCase().includes(q) ||
      (a.GekozenVervoerder||'').toLowerCase().includes(q) ||
      (a.LosLand||'').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [gefilterd, zoek]);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      <div>
        <Label>Filter</Label>
        <Select value={filter} onChange={e=>setFilter(e.target.value)}>
          <option value="alle">Alle zendingen</option>
          <option value="deze-week">Deze week</option>
          <option value="deze-maand">Deze maand</option>
          <option value="dit-kwartaal">Dit kwartaal</option>
          <option value="lopend">Lopende zendingen</option>
          <option value="aangepast">Aangepaste periode</option>
        </Select>
      </div>
      {filter==='aangepast' && <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <div style={{flex:1}}>
          <Label>Van</Label>
          <Input type="date" value={vanD} onChange={e=>setVanD(e.target.value)}/>
        </div>
        <div style={{flex:1}}>
          <Label>Tot</Label>
          <Input type="date" value={totD} onChange={e=>setTotD(e.target.value)}/>
        </div>
      </div>}
      <div>
        <Label>Zoek zending</Label>
        <Input placeholder="ID, plaats, land of vervoerder…" value={zoek} onChange={e=>setZoek(e.target.value)}/>
      </div>
      <div style={{fontSize:11,color:G.txt3}}>{lijst.length} zending{lijst.length!==1?'en':''} gevonden</div>
      <div style={{border:`1px solid ${G.borderL}`,borderRadius:8,overflow:'hidden',maxHeight:260,overflowY:'auto'}}>
        {lijst.map((a,i)=>(
          <div key={i} onClick={()=>setCfg(c=>({...c,gekozen:a}))} style={{
            padding:'7px 12px',cursor:'pointer',borderBottom:`1px solid #f5f5f5`,
            background:cfg.gekozen?.AanvraagID===a.AanvraagID?'#eef2ff':i%2===0?G.white:'#fafafa',
          }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <span style={{fontWeight:700,color:G.navy,marginRight:8,fontSize:12}}>{a.AanvraagID}</span>
                <span style={{fontSize:11,color:G.txt}}>{a.LaadPlaats||a.LaadLand||'—'} → {a.LosPlaats||a.LosLand||'—'}</span>
              </div>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                <span style={{fontSize:10,color:G.txt3}}>{a.LaadDatum?String(a.LaadDatum).slice(0,10).split('-').reverse().join('-'):'—'}</span>
                {cfg.gekozen?.AanvraagID===a.AanvraagID&&<span style={{color:G.green,fontWeight:700}}>✓</span>}
              </div>
            </div>
            {a.GekozenVervoerder&&<div style={{fontSize:10,color:G.txt3,marginTop:2}}>{a.GekozenVervoerder} · {a.Status||''}</div>}
          </div>
        ))}
        {lijst.length===0&&<div style={{padding:'16px',textAlign:'center',color:G.txt3,fontSize:13}}>Geen zendingen gevonden</div>}
      </div>
    </div>
  );
}

function ConfigFacturen({cfg, setCfg, facturen}) {
  const [periodeActief, setPeriodeActief] = useState('Alles');
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <PeriodKnoppen van={cfg.factuurVan||''} setVan={v=>setCfg(c=>({...c,factuurVan:v}))} tot={cfg.factuurTot||''} setTot={v=>setCfg(c=>({...c,factuurTot:v}))} actief={periodeActief} setActief={setPeriodeActief}/>
      <div>
        <Label>Filter</Label>
        <Select value={cfg.filter||'alle'} onChange={e=>setCfg(c=>({...c,filter:e.target.value}))}>
          <option value="alle">Alle facturen</option>
          <option value="open">Alleen openstaand</option>
          <option value="betaald">Alleen betaald</option>
        </Select>
      </div>
      <div>
        <Label>Sortering</Label>
        <Select value={cfg.sorteer||'datum-desc'} onChange={e=>setCfg(c=>({...c,sorteer:e.target.value}))}>
          <option value="datum-desc">Datum (nieuwste eerst)</option>
          <option value="datum-asc">Datum (oudste eerst)</option>
          <option value="bedrag-desc">Bedrag (hoog-laag)</option>
        </Select>
      </div>
    </div>
  );
}

// ─── Live A4 Preview ──────────────────────────────────────────────────────────
function A4Preview({html, orientatie}) {
  const containerRef = React.useRef(null);
  const [scale, setScale] = React.useState(0.5);
  const liggend = (orientatie||'staand') === 'liggend';

  // A4 afmetingen — staand: 794×1123px, liggend: 1123×794px
  const A4W = liggend ? 1123 : 794;
  const A4H = liggend ? 794  : 1123;
  const padH = liggend ? '38px 38px' : '53px 45px';

  // Hooks ALTIJD voor early return
  React.useEffect(() => {
    const calcScale = () => {
      if(containerRef.current) {
        const avail = containerRef.current.clientWidth - 24;
        setScale(Math.min(avail / A4W, 0.95));
      }
    };
    calcScale();
    window.addEventListener('resize', calcScale);
    return () => window.removeEventListener('resize', calcScale);
  }, [liggend, A4W]);

  if(!html) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:G.txt3,flexDirection:'column',gap:8}}>
      <div style={{fontSize:40}}>📄</div>
      <div style={{fontSize:13}}>Selecteer een rapporttype</div>
    </div>
  );

  const fullHtml = `<!DOCTYPE html><html lang="nl"><head><meta charset="UTF-8">
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:-apple-system,Arial,sans-serif;font-size:${liggend?'9':'10'}px;
           color:#1e293b;background:white;padding:${padH};width:${A4W}px;}
    </style>
  </head><body>${html}</body></html>`;

  return (
    <div ref={containerRef} style={{
      width:'100%', height:'100%', overflow:'auto',
      background:'#94a3b8', padding:'12px',
      display:'flex', justifyContent:'center', alignItems:'flex-start',
    }}>
      {/* Wrapper op geschaalde hoogte zodat scrollbar klopt */}
      <div style={{
        width: Math.round(A4W * scale) + 'px',
        height: Math.round(A4H * scale) + 'px',
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Echt A4 vel op ware grootte, dan getransformeerd */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0,
          width: A4W + 'px',
          height: A4H + 'px',
          transformOrigin: 'top left',
          transform: `scale(${scale})`,
          background: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,.25)',
          overflow: 'hidden',
        }}>
          <iframe
            key={`${liggend}-${A4W}`}
            srcDoc={fullHtml}
            style={{
              width: A4W + 'px',
              height: A4H + 'px',
              border: 'none',
              display: 'block',
            }}
            title="Print preview"
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Hoofd modal ──────────────────────────────────────────────────────────────
export default function PrintDialog({ sectie='home', aanvragen=[], facturen: facturenProp=[], klantInfo={}, onClose }) {
  const [pos, setPos] = React.useState(null); // null = gecentreerd, {x,y} = verplaatst
  const dragRef = React.useRef(null);

  const onDragStart = (e) => {
    const rect = dragRef.current?.getBoundingClientRect();
    if(!rect) return;
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;
    const onMove = (ev) => {
      setPos({ x: ev.clientX - startX, y: ev.clientY - startY });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  };
  // Laad facturen intern als ze niet via prop worden meegegeven
  const [facturenLoaded, setFacturenLoaded] = useState(facturenProp);
  useEffect(() => {
    if(facturenProp && facturenProp.length > 0) {
      setFacturenLoaded(facturenProp);
      return;
    }
    // Interne dashboard API: /api/facturen/klant/:klantNaam
    // Haal klantNaam uit klantInfo of uit de eerste aanvraag
    const klantNaam = klantInfo?.naam || klantInfo?.bedrijfsnaam || klantInfo?.Bedrijfsnaam
      || (aanvragen.length > 0 ? (aanvragen[0].Klant || '') : '');
    if(klantNaam) {
      fetch(`/api/facturen/klant/${encodeURIComponent(klantNaam)}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if(d?.facturen?.length) setFacturenLoaded(d.facturen); })
        .catch(() => {});
    } else {
      // Fallback: klantenportaal API
      fetch('/api/customer/facturen', { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if(d?.facturen?.length) setFacturenLoaded(d.facturen); })
        .catch(() => {});
    }
  }, [facturenProp, klantInfo]);
  const facturen = facturenLoaded;

  const bedrijfsnaam = klantInfo?.bedrijfsnaam || klantInfo?.Bedrijfsnaam || klantInfo?.naam || 'Klant';
  const logo = (() => { try { return localStorage.getItem('sendwell_klant_logo') || null; } catch(e) { return null; } })();

  // Filter rapporten op basis van actieve sectie
  const beschikbaar = RAPPORT_TYPES; // Altijd alle rapporttypes tonen
  const [gekozenType, setGekozenType] = useState(beschikbaar[0]?.id || 'zendingen');

  // Config per type
  const [cfg, setCfg] = useState({
    jaar: ditJaar,
    vergelijkJaar: ditJaar-1,
    van: startJaar(),
    tot: vandaag(),
    statussen: ['alle'],
    sorteer: 'datum-desc',
    inclKpi: true, inclGrafiek: true, grafiekType: 'balk', inclBestemming: true, grafiekTypeBestemming:'hbalk', grafiekTypeRoutes:'hbalk', grafiekTypeGoed:'taart', dashPeriodeType:'jaar', kwartaal:Math.floor(ditMaand/3)+1, maand:ditMaand, dashVan:'', dashTot:'', orientatie:'liggend',
    inclRoutes: true, inclGoed: true, inclVergelijk: true,
    inclMaand: true, inclKosten: true, inclVolume: true,
    inclGewicht: true, inclRef: false,
    filter: 'alle',
    gekozen: null,
  });

  // Live preview HTML
  const previewHtml = useMemo(() => {
    try {
      return generateHTML(gekozenType, aanvragen, facturen, bedrijfsnaam, cfg, true, logo);
    } catch(e) { return '<p style="color:red;padding:20px">Preview fout: '+e.message+'</p>'; }
  }, [gekozenType, aanvragen, facturen, bedrijfsnaam, cfg]);

  // Teller
  const aantalInSelectie = useMemo(() => {
    if(gekozenType==='zending_detail') return cfg.gekozen ? 1 : 0;
    if(gekozenType==='facturen') {
      let l=facturen;
      if(cfg.filter==='open') l=l.filter(a=>(a.FactuurStatus||a.Status)!=='Betaald');
      if(cfg.filter==='betaald') l=l.filter(a=>(a.FactuurStatus||a.Status)==='Betaald');
      if(cfg.van) l=l.filter(a=>(a.Datum||'')>=cfg.van);
      if(cfg.tot) l=l.filter(a=>(a.Datum||'')<=cfg.tot);
      return l.length;
    }
    let l=aanvragen;
    if(cfg.van) l=l.filter(a=>(a.Datum||a.LaadDatum||'')>=cfg.van);
    if(cfg.tot) l=l.filter(a=>(a.Datum||a.LaadDatum||'')<=cfg.tot);
    if(cfg.statussen&&!cfg.statussen.includes('alle')) l=l.filter(a=>cfg.statussen.includes(statusLabel(a)));
    return l.length;
  },[gekozenType,aanvragen,facturen,cfg]);

  // ─── Word opslaan (HTML-blob als .doc, geen SVG) ─────────────────────────────
  const doSaveWord = () => {
    const wordHtml = generateWordHTML(gekozenType, aanvragen, facturen, bedrijfsnaam, cfg, logo);
    const titel = RAPPORT_TYPES.find(r=>r.id===gekozenType)?.titel||'Rapport';
    const blob = new Blob([wordHtml], {type:'application/msword;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (titel + '-' + bedrijfsnaam).replace(/[^a-zA-Z0-9\s]/g, '-').trim() + '.doc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(()=>URL.revokeObjectURL(url), 2000);
  };

  // ─── PDF opslaan via html2pdf.js (CDN) — geen printvenster zichtbaar ────────
  const doSavePdf = () => {
    const html = generateHTML(gekozenType, aanvragen, facturen, bedrijfsnaam, cfg, true, logo);
    const titel = RAPPORT_TYPES.find(r=>r.id===gekozenType)?.titel||'Rapport';
    const liggend = (cfg.orientatie||'staand') === 'liggend';
    const bestandsnaam = (titel + '-' + bedrijfsnaam).replace(/[^a-zA-Z0-9\s]/g, '-').trim() + '.pdf';

    // Maak een verborgen container met de HTML
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:' + (liggend?'297mm':'210mm') + ';background:white;font-family:Arial,sans-serif;font-size:10px;color:#1e293b;';
    container.innerHTML = html;
    document.body.appendChild(container);

    // Laad html2pdf.js dynamisch van CDN als die nog niet geladen is
    const loadAndPrint = () => {
      const opt = {
        margin: liggend ? [8,10,8,10] : [14,12,14,12],
        filename: bestandsnaam,
        image: { type:'jpeg', quality:0.95 },
        html2canvas: { scale:2, useCORS:true, letterRendering:true },
        jsPDF: { unit:'mm', format:'a4', orientation: liggend?'landscape':'portrait' },
        pagebreak: { mode:['avoid-all','css','legacy'] },
      };
      window.html2pdf().set(opt).from(container).save().then(() => {
        document.body.removeChild(container);
      }).catch(() => {
        document.body.removeChild(container);
      });
    };

    if(window.html2pdf) {
      loadAndPrint();
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = loadAndPrint;
      script.onerror = () => {
        document.body.removeChild(container);
        alert('PDF bibliotheek kon niet geladen worden. Probeer Afdrukken → Opslaan als PDF.');
      };
      document.head.appendChild(script);
    }
  };

  const doPrint = () => {
    const html = generateHTML(gekozenType, aanvragen, facturen, bedrijfsnaam, cfg, true, logo);
    const titel = RAPPORT_TYPES.find(r=>r.id===gekozenType)?.titel||'Rapport';

    // Verborgen iframe — geen zichtbaar HTML venster
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="nl"><head><meta charset="UTF-8">
      <title>${titel} — SendWell</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:-apple-system,Arial,sans-serif;font-size:11px;color:#1e293b;background:white;}
        @media print{body{margin:0;}}
      </style>
    </head><body>${html}</body></html>`);
    doc.close();

    // Wacht tot iframe geladen is, dan printen
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch(e) {}
      // Verwijder iframe na korte vertraging
      setTimeout(() => {
        if(document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 2000);
    };
    // Modal blijft open
  };

  useEffect(()=>{
    const h=e=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',h);
    return()=>window.removeEventListener('keydown',h);
  },[onClose]);

  const actType = RAPPORT_TYPES.find(r=>r.id===gekozenType);
  const kanPrinten = gekozenType==='zending_detail' ? !!cfg.gekozen : aantalInSelectie>0;

  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.55)',display:'flex',alignItems:'center',justifyContent:'center',padding:12}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div ref={dragRef} style={{
        background:G.white,borderRadius:14,width:'100%',
        maxWidth:(cfg.orientatie||'staand')==='liggend'?1300:1100,
        height:'90vh',
        display:'flex',flexDirection:'column',
        boxShadow:'0 32px 80px rgba(0,0,0,.3)',overflow:'hidden',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? {top:pos.y, left:pos.x, margin:0} : {}),
      }}>

        {/* ── Header ── */}
        <div onMouseDown={onDragStart} style={{display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'14px 20px',background:G.navy,flexShrink:0,cursor:'move',userSelect:'none'}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:36,height:36,background:'rgba(255,255,255,.1)',borderRadius:8,
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>🖨</div>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:'#fff'}}>Afdrukken</div>
              <div style={{fontSize:11,color:'rgba(255,255,255,.5)'}}>
                {actType?.titel||'—'} · {
                  gekozenType==='zending_detail' ? (cfg.gekozen ? `Zending ${cfg.gekozen.AanvraagID}` : 'Selecteer een zending') :
                  gekozenType==='dashboard'||gekozenType==='maandrapport'||gekozenType==='jaaroverzicht' ? `${aantalInSelectie} zendingen in periode` :
                  gekozenType==='facturen' ? `${aantalInSelectie} facturen` :
                  `${aantalInSelectie} zendingen`
                }
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button onClick={doSaveWord} disabled={!kanPrinten} style={{
              display:'flex',alignItems:'center',gap:6,
              background:kanPrinten?'rgba(255,255,255,.12)':'rgba(255,255,255,.05)',
              color:kanPrinten?'rgba(255,255,255,.9)':'rgba(255,255,255,.3)',
              border:`1px solid ${kanPrinten?'rgba(255,255,255,.25)':'rgba(255,255,255,.1)'}`,
              borderRadius:8,padding:'8px 14px',fontSize:12,fontWeight:600,
              cursor:kanPrinten?'pointer':'not-allowed',
            }}>
              📝 Word
            </button>
            <button onClick={doSavePdf} disabled={!kanPrinten} style={{
              display:'flex',alignItems:'center',gap:6,
              background:kanPrinten?'rgba(255,255,255,.12)':'rgba(255,255,255,.05)',
              color:kanPrinten?'rgba(255,255,255,.9)':'rgba(255,255,255,.3)',
              border:`1px solid ${kanPrinten?'rgba(255,255,255,.25)':'rgba(255,255,255,.1)'}`,
              borderRadius:8,padding:'8px 14px',fontSize:12,fontWeight:600,
              cursor:kanPrinten?'pointer':'not-allowed',
            }}>
              📄 PDF
            </button>
            <button onClick={doPrint} disabled={!kanPrinten} style={{
              display:'flex',alignItems:'center',gap:7,
              background:kanPrinten?G.gold:'rgba(255,255,255,.1)',
              color:kanPrinten?G.navy:'rgba(255,255,255,.4)',
              border:'none',borderRadius:8,padding:'9px 20px',
              fontSize:13,fontWeight:700,cursor:kanPrinten?'pointer':'not-allowed',
              transition:'.15s',
            }}>
              🖨 Afdrukken
            </button>
            <button onClick={onClose} style={{background:'rgba(255,255,255,.1)',border:'none',
              color:'#fff',width:32,height:32,borderRadius:8,cursor:'pointer',fontSize:18,
              display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
          </div>
        </div>

        {/* ── Body: drie kolommen ── */}
        <div style={{flex:1,display:'flex',minHeight:0}}>

          {/* Kolom 1: rapport type keuze */}
          <div style={{width:200,flexShrink:0,borderRight:`1px solid ${G.borderL}`,
            overflowY:'auto',background:G.cream,padding:'12px 8px'}}>
            <div style={{fontSize:10,fontWeight:700,color:G.txt3,textTransform:'uppercase',
              letterSpacing:'.08em',padding:'4px 8px 8px'}}>Rapporttype</div>
            {beschikbaar.map(r=>(
              <button key={r.id} onClick={()=>setGekozenType(r.id)} style={{
                width:'100%',textAlign:'left',padding:'10px 10px',borderRadius:8,
                border:`1px solid ${gekozenType===r.id?G.navy:G.borderL}`,
                background:gekozenType===r.id?G.navy:G.white,
                marginBottom:5,cursor:'pointer',transition:'.12s',
              }}>
                <div style={{fontSize:18,marginBottom:4}}>{r.icon}</div>
                <div style={{fontSize:12.5,fontWeight:600,color:gekozenType===r.id?'#fff':G.txt,lineHeight:1.2}}>{r.titel}</div>
                <div style={{fontSize:10.5,color:gekozenType===r.id?'rgba(255,255,255,.65)':G.txt3,marginTop:3,lineHeight:1.3}}>{r.sub}</div>
              </button>
            ))}
          </div>

          {/* Kolom 2: configuratie */}
          <div style={{width:290,flexShrink:0,borderRight:`1px solid ${G.borderL}`,
            display:'flex',flexDirection:'column',minHeight:0}}>
            {/* Scrollbare opties */}
            <div style={{flex:1,overflowY:'auto',padding:'16px 14px 8px'}}>
              <div style={{fontSize:10,fontWeight:700,color:G.txt3,textTransform:'uppercase',
                letterSpacing:'.08em',marginBottom:12}}>Opties</div>
              {gekozenType==='dashboard'     && <ConfigDashboard     cfg={cfg} setCfg={setCfg}/>}
              {gekozenType==='zendingen'     && <ConfigZendingen     cfg={cfg} setCfg={setCfg}/>}
              {gekozenType==='maandrapport'  && <ConfigMaandrapport  cfg={cfg} setCfg={setCfg}/>}
              {gekozenType==='jaaroverzicht' && <ConfigJaaroverzicht cfg={cfg} setCfg={setCfg}/>}
              {gekozenType==='zending_detail'&& <ConfigZendingDetail cfg={cfg} setCfg={setCfg} aanvragen={aanvragen}/>}
              {gekozenType==='facturen'      && <ConfigFacturen      cfg={cfg} setCfg={setCfg} facturen={facturen}/>}
            </div>
            {/* Altijd zichtbaar: oriëntatie */}
            <div style={{padding:'12px 14px',borderTop:`1px solid ${G.borderL}`,background:G.cream,flexShrink:0}}>
              <div style={{fontSize:10,fontWeight:700,color:G.txt3,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>Papierformaat</div>
              <div style={{display:'flex',gap:4}}>
                {[['liggend','⟺ Liggend'],['staand','↕ Staand']].map(([type,lbl])=>(
                  <button key={type} onClick={()=>setCfg(c=>({...c,orientatie:type}))} style={{
                    flex:1,padding:'6px 4px',borderRadius:8,cursor:'pointer',fontSize:11.5,
                    border:`1px solid ${(cfg.orientatie||'liggend')===type?G.navy:G.borderL}`,
                    background:(cfg.orientatie||'liggend')===type?G.navy:G.white,
                    color:(cfg.orientatie||'liggend')===type?'#fff':G.txt2,
                    fontWeight:(cfg.orientatie||'liggend')===type?600:400,
                  }}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Kolom 3: live A4 preview */}
          <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>
            <div style={{padding:'8px 14px',borderBottom:`1px solid ${G.borderL}`,background:G.cream,
              fontSize:10,fontWeight:600,color:G.txt3,textTransform:'uppercase',letterSpacing:'.08em',flexShrink:0}}>
              Live preview — A4
            </div>
            <div style={{flex:1,overflow:'hidden'}}>
              <A4Preview html={previewHtml} orientatie={cfg.orientatie}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
